import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { ApiError } from "../api/client";
import {
  useCLITokens,
  useDeviceDecision,
  useDeviceInfo,
  useMe,
  useRevokeCLIToken,
} from "../api/queries";
import { Icon } from "../components/Icons";

/**
 * The CLI approval screen — the browser half of the device authorization grant.
 *
 * This screen is a security control, not a confirmation dialog, and it is
 * written that way. Approving here mints a long-lived credential on a machine
 * that is not necessarily the one you are reading this on, and the classic
 * attack on this flow is social: someone sends you a link with THEIR code in
 * it, you click approve without reading, and their laptop now has a token into
 * your workspace.
 *
 * Three things defend against that, and all three are deliberate:
 *
 *  1. The code is displayed large, so it can be compared against the terminal.
 *     A user who did not start a login has nothing to compare it to.
 *  2. The requesting machine's self-reported name and IP are shown, labelled
 *     as unverified, because they are.
 *  3. Approval requires choosing a workspace. There is no default and no
 *     single-click path — the extra decision is what turns a reflex into a
 *     reading.
 */
export function DeviceApproval() {
  const [params, setParams] = useSearchParams();
  const { data: me } = useMe();

  // The code may arrive in the URL (the CLI opened a browser) or be typed (the
  // CLI printed it and the user navigated here). Both paths converge on this
  // one piece of state.
  const [code, setCode] = useState(() => params.get("code") ?? "");
  const [submitted, setSubmitted] = useState(() => params.get("code") ?? "");

  const info = useDeviceInfo(submitted || null);
  const decision = useDeviceDecision(submitted);

  const workspaces = me?.workspaces ?? [];
  const [workspaceSlug, setWorkspaceSlug] = useState("");

  // Preselect only when there is no choice to make. With several workspaces a
  // default is a trap: it is the one field a user skims past, and skimming it
  // grants a credential to the wrong tenant.
  useEffect(() => {
    if (workspaces.length === 1) setWorkspaceSlug(workspaces[0].slug);
  }, [workspaces]);

  if (decision.isSuccess) {
    return <Decided approved={decision.data.approved} slug={decision.data.workspace_slug} />;
  }

  return (
    <div className="cli-page">
      <header className="cli-hero">
        <div className="cli-hero-icon"><Icon name="terminal" /></div>
        <div>
          <div className="eyebrow">CLI setup · Step 1 of 1</div>
          <h1>Connect your terminal</h1>
          <p>
            Confirm this sign-in request to securely connect the BERBench CLI
            to your workspace.
          </p>
        </div>
      </header>

      <section className="cli-approval-card">
        <div className="cli-card-head">
          <div>
            <span className="eyebrow">Verify the request</span>
            <h2>Does this code match your terminal?</h2>
          </div>
          <span className="cli-secure-badge"><Icon name="lock" /> Secure sign-in</span>
        </div>

        <form
          className="device-code-form"
          onSubmit={(e) => {
            e.preventDefault();
            const next = code.trim().toUpperCase();
            setSubmitted(next);
            // Keep the URL in step so a reload does not lose the code.
            setParams(next ? { code: next } : {}, { replace: true });
          }}
        >
          <label htmlFor="device-code">Device code</label>
          <div className="device-code-row">
            <input
              id="device-code"
              className="device-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
              maxLength={9}
            />
            <button type="submit" disabled={code.trim() === ""}>
              Find request
            </button>
          </div>
        </form>

        {info.isPending && submitted !== "" && (
          <p className="device-status" role="status">
            Checking that code…
          </p>
        )}

        {info.isError && (
          <p className="device-status device-status-error" role="alert">
            {info.error instanceof ApiError && info.error.status === 404
              ? "That code is not valid, or it has expired. Start the login again with `berbench login`."
              : "We could not check that code. Please try again."}
          </p>
        )}

        {info.data && (
          <>
            <div className="device-request">
              <div className="device-code-panel">
                <span>Code shown in your terminal</span>
                <strong className="device-code-display" aria-label="Code awaiting approval">
                  {info.data.user_code}
                </strong>
                <small>Compare every character before continuing.</small>
              </div>

              <dl className="account-facts">
                <div>
                  <dt>Requesting machine</dt>
                  {/* Reported by the requesting client and never verified. Said
                      plainly, because a user deciding whether to trust it needs
                      to know it proves nothing. */}
                  <dd>{info.data.client_name || "Not reported"}</dd>
                </div>
                <div>
                  <dt>Network address</dt>
                  <dd>{info.data.client_ip || "Unknown"}</dd>
                </div>
                <div>
                  <dt>Request expires</dt>
                  <dd>{new Date(info.data.expires_at).toLocaleTimeString()}</dd>
                </div>
              </dl>
            </div>

            <div className="device-warning" role="note">
              <Icon name="lock" />
              <div>
                <strong>Only approve a request you started</strong>
                <p>
                  If you did not just run <code>berbench login</code>, deny this
                  request. Machine details are self-reported and not verified.
                </p>
              </div>
            </div>

            <div className="device-workspace-field">
              <label className="device-workspace" htmlFor="device-workspace">
                Connect to workspace
              </label>
              <p>The CLI can upload results only to the workspace you choose.</p>
              <select
                id="device-workspace"
                value={workspaceSlug}
                onChange={(e) => setWorkspaceSlug(e.target.value)}
              >
                <option value="">Choose a workspace…</option>
                {workspaces.map((w) => (
                  <option key={w.slug} value={w.slug}>
                    {w.name} ({w.role})
                  </option>
                ))}
              </select>
            </div>

            {decision.isError && (
              <p className="device-status device-status-error" role="alert">
                {decision.error instanceof Error
                  ? decision.error.message
                  : "That did not work. Please try again."}
              </p>
            )}

            <div className="device-actions">
              <button
                type="button"
                className="device-deny"
                disabled={decision.isPending}
                onClick={() => decision.mutate({ approve: false })}
              >
                Deny
              </button>
              <button
                type="button"
                className="device-approve"
                disabled={decision.isPending || workspaceSlug === ""}
                onClick={() => decision.mutate({ approve: true, workspaceSlug })}
              >
                {decision.isPending ? "Connecting…" : "Connect CLI"}
              </button>
            </div>
          </>
        )}
      </section>

      <ActiveCLILogins hideWhenEmpty />
    </div>
  );
}

function Decided({ approved, slug }: { approved: boolean; slug: string }) {
  return (
    <div className="cli-page cli-result-page">
      <section className={`cli-result ${approved ? "approved" : "denied"}`}>
        <div className="cli-result-mark">
          <Icon name={approved ? "check" : "lock"} />
        </div>
        <div className="eyebrow">{approved ? "Connection complete" : "Request closed"}</div>
        <h1>{approved ? "Your CLI is connected" : "Request denied"}</h1>
        <p>
          {approved
            ? `Your terminal can now upload benchmark results to ${slug}.`
            : "No access was granted to this machine."}
        </p>

        {approved && (
          <div className="cli-next-step">
            <div>
              <span>Back in your terminal</span>
              <strong>Start with a repository</strong>
            </div>
            <code><i aria-hidden="true">$</i> berbench init</code>
          </div>
        )}

        <p className="cli-close-note">You can close this tab and return to your terminal.</p>
      </section>
      <ActiveCLILogins />
    </div>
  );
}

/**
 * Every CLI login this account has, with a way to end each one.
 *
 * It sits on this screen rather than a settings page because this is where
 * someone already is when they are thinking about CLI access — and because a
 * credential nobody can see is a credential nobody revokes.
 */
function ActiveCLILogins({ hideWhenEmpty = false }: { hideWhenEmpty?: boolean }) {
  const { data } = useCLITokens();
  const revoke = useRevokeCLIToken();

  const tokens = data?.tokens ?? [];

  if (hideWhenEmpty && tokens.length === 0) return null;

  return (
    <section className="cli-machines">
      <div className="cli-machines-head">
        <span>
          <Icon name="repo" /> Connected machines
        </span>
        {tokens.length > 0 && <small>{tokens.length} active</small>}
      </div>

      {tokens.length === 0 ? (
        <p className="device-status">No machines are connected to this account.</p>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Machine</th>
              <th>Workspace</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id}>
                <td>{t.label || "Unnamed"}</td>
                <td>{t.workspace_name}</td>
                <td>{new Date(t.last_used_at).toLocaleDateString()}</td>
                <td>
                  <button
                    type="button"
                    className="device-deny"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(t.id)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
